import React from 'react';
import { motion } from 'framer-motion';
import { Brain, Target, Shield, TrendingUp } from 'lucide-react';
export function CJIPage() {
  return <div className="min-h-screen bg-surface-bg" data-id="element-163" data-mp="l822a71uxvgb">
      {/* Hero Section */}
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-28 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto" data-id="element-164" data-mp="78atckq77596">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="text-center" data-id="element-165" data-mp="b9ciu9ubc2xy">
          <div className="inline-flex items-center gap-2 mb-6" data-id="element-166" data-mp="q6n4gc0o6p7e">
            <span className="w-8 h-px bg-teal" data-id="element-167" data-mp="q40ts3ihftky"></span>
            <span className="text-xs font-semibold text-teal uppercase tracking-[0.2em]" data-id="element-168" data-mp="u6495389vibi">
              Civic Judgment Index
            </span>
            <span className="w-8 h-px bg-teal" data-id="element-169" data-mp="1hv6scfik1j84"></span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-ink-dark leading-tight mb-8" data-id="element-170" data-mp="1mp1672bhb3za">
            The Analytical Core of Civic Judgment
          </h1>

          <p className="text-xl text-ink-muted leading-relaxed max-w-3xl mx-auto" data-id="element-171" data-mp="36jrqvbbmddyk">
            The Civic Judgment Index (CJI) is the foundational analytical layer
            of the Human Capability Platform. It is designed to measure and strengthen
            reasoning quality, decision competence, manipulation resistance, and
            leadership judgment across civic, institutional, and
            high-responsibility environments.
          </p>
        </motion.div>
      </section>

      {/* Introduction */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-172" data-mp="801nsyhlbnjr">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="prose prose-lg max-w-none" data-id="element-173" data-mp="32rn2mor3pgm">
          <p className="text-lg text-ink-muted leading-relaxed mb-6" data-id="element-174" data-mp="dp1igdpatacj">
            Modern societies rely on judgment everywhere, but rarely have a
            clear framework for evaluating it. People are expected to make
            decisions under uncertainty, sort signal from noise, resist
            emotional manipulation, and act with foresight in environments
            shaped by complexity, pressure, and competing narratives.
          </p>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-175" data-mp="wmcjo3uhqdum">
            CJI was created as the foundational analytical structure for
            understanding how people reason, how they respond to persuasion and
            pressure, and how decision quality can be strengthened over time.
          </p>
        </motion.div>
      </section>

      {/* Why CJI Exists */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-surface-muted border-y border-line" data-id="element-176" data-mp="1fdlx2o7dtmrh">
        <div className="max-w-4xl mx-auto" data-id="element-177" data-mp="1pilrcobaza4k">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-178" data-mp="1pjn0uoco7ok6">
            <div className="flex items-center gap-4 mb-6" data-id="element-179" data-mp="289cxzi3womf9">
              <div className="w-12 h-12 rounded-lg bg-navy flex items-center justify-center text-white" data-id="element-180" data-mp="28axqz3aknxb0">
                <Target size={24} data-id="element-181" data-mp="23c2z7bummyer" />
              </div>
              <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-182" data-mp="1yhytevx3kwf9">
                Why CJI Exists
              </h2>
            </div>
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-183" data-mp="1ufly71fz8gwq">
              Judgment is one of the most important variables in modern society,
              yet institutions often evaluate credentials, outputs, or
              compliance without examining the underlying quality of reasoning
              that shapes decisions. Weak foresight, emotional reactivity, and
              manipulation vulnerability can create consequences far beyond a
              single moment. CJI exists to provide a more structured way to
              assess the core dimensions of judgment that matter in civic,
              professional, institutional, and leadership settings.
            </p>
          </motion.div>
        </div>
      </section>

      {/* What CJI Measures */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-184" data-mp="2ufntsjynsltg">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-185" data-mp="2gnpq4onpvi4o">
          <div className="flex items-center gap-4 mb-6" data-id="element-186" data-mp="3d7gmsedqa716">
            <div className="w-12 h-12 rounded-lg bg-teal flex items-center justify-center text-white" data-id="element-187" data-mp="11repwh5nurht">
              <Brain size={24} data-id="element-188" data-mp="wsuzo4k1atjn" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-189" data-mp="ws21vt31zedn">
              What CJI Measures
            </h2>
          </div>
          <p className="text-lg text-ink-muted leading-relaxed mb-8" data-id="element-190" data-mp="16iejpdjhgtg3">
            CJI focuses on the key dimensions that shape civic judgment and
            practical decision-making. These include:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-191" data-mp="du8zcrwcd6gs">
            {[{
            name: 'Reasoning quality',
            link: '/reasoning-quality'
          }, {
            name: 'Consequential thinking',
            link: '/consequential-thinking'
          }, {
            name: 'Manipulation resistance',
            link: '/manipulation-resistance'
          }, {
            name: 'Emotional regulation',
            link: '/emotional-regulation'
          }, {
            name: 'Social cooperation',
            link: '/social-cooperation'
          }, {
            name: 'Leadership responsibility',
            link: '/leadership-responsibility'
          }].map((item, index) => <motion.a key={item.name} href={item.link} initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: index * 0.1
          }} className="flex items-center gap-3 bg-white p-4 rounded-lg border border-line hover:border-navy hover:shadow-md transition-all group" data-id="element-192" data-mp="1nk0wqf8vp12p" data-mpi={index}>
                <div className="w-2 h-2 rounded-full bg-navy" data-id="element-193" data-mp="3n1uk16prombu"></div>
                <span className="text-ink-dark font-medium group-hover:text-navy transition-colors" data-id="element-194" data-mp="3j0sey9965g1y">
                  {item.name}
                </span>
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity text-navy" data-id="element-195" data-mp="3e3sdkbn9l1h6">
                  <path d="M6 12L10 8L6 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-196" data-mp="2vb7vzqd0miso" />
                </svg>
              </motion.a>)}
          </div>
          <p className="text-lg text-ink-muted leading-relaxed mt-8" data-id="element-197" data-mp="2gdxdhx1ygcng">
            Together, these domains help create a more rigorous picture of how
            judgment functions under complexity, pressure, and responsibility.
          </p>
        </motion.div>
      </section>

      {/* The Role of CJI */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white border-y border-line" data-id="element-198" data-mp="3v95go6zdsofm">
        <div className="max-w-4xl mx-auto" data-id="element-199" data-mp="434cmidu2v3h">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-200" data-mp="422qniva4o6z">
            <div className="flex items-center gap-4 mb-6" data-id="element-201" data-mp="3hiacv93qoxiw">
              <div className="w-12 h-12 rounded-lg bg-navy-light flex items-center justify-center text-white" data-id="element-202" data-mp="3hgpknq4wdqo7">
                <Shield size={24} data-id="element-203" data-mp="32nvt36ccpk36" />
              </div>
              <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-204" data-mp="37jb3tpf2azvd">
                The Role of CJI in the Platform
              </h2>
            </div>
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-205" data-mp="2jximemxdqdc2">
              CJI serves as the analytical base layer of the Human Capability
              Platform. It is the platform's core engine for decision-quality analytics,
              propaganda-resistance profiling, cognitive pattern assessment, and
              leadership reasoning diagnostics. Where NCJI expands into broader
              developmental pathways, lifecycle architecture, and institutional
              deployment, CJI provides the essential measurement logic that
              makes those higher-order applications possible.
            </p>
          </motion.div>
        </div>
      </section>

      {/* How CJI Creates Value */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-206" data-mp="3qkw9ucjt1sku">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-207" data-mp="3lml1y7d2ls59">
          <div className="flex items-center gap-4 mb-6" data-id="element-208" data-mp="e6ulacdhr1lx">
            <div className="w-12 h-12 rounded-lg bg-green-soft flex items-center justify-center text-white" data-id="element-209" data-mp="e7dxrdkdgxsk">
              <TrendingUp size={24} data-id="element-210" data-mp="a3fy91r4xkyz" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-211" data-mp="16uqj5kesqzka">
              How CJI Creates Value
            </h2>
          </div>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-212" data-mp="11um31vhsw6ul">
            CJI is designed to make judgment more visible, more structured, and
            more actionable. It helps individuals, institutions, and leadership
            environments better understand where reasoning is strong, where
            vulnerabilities exist, and where development is needed. By
            identifying patterns, clarifying risks, and supporting stronger
            decision-making, CJI creates a more useful analytical foundation for
            growth and readiness.
          </p>
        </motion.div>
      </section>

      {/* Use Cases */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-surface-muted border-y border-line" data-id="element-213" data-mp="224d9o94cncp5">
        <div className="max-w-5xl mx-auto" data-id="element-214" data-mp="26w53qtmps0k1">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-215" data-mp="26x6hx6z1135z">
            <h2 className="text-3xl font-heading font-bold text-ink-dark mb-12 text-center" data-id="element-216" data-mp="2pu7miqoo519e">
              Use Cases
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-217" data-mp="rfwiuwh2btw1">
              {[{
              title: 'Education',
              desc: 'Support reasoning, media literacy, and judgment development',
              link: '/education'
            }, {
              title: 'Employers & Organizations',
              desc: 'Strengthen decision quality, communication, and advancement readiness',
              link: '/employers'
            }, {
              title: 'Civil Society',
              desc: 'Support misinformation resilience and collective reasoning capacity',
              link: '/civil-society'
            }, {
              title: 'Government',
              desc: 'Structured framework for understanding readiness and judgment quality',
              link: '/government'
            }, {
              title: 'Leadership',
              desc: 'High-responsibility decision-making and executive evaluation',
              link: '/leadership'
            }].map((useCase, index) => <motion.a key={useCase.title} href={useCase.link} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: index * 0.1
            }} className="bg-white p-6 rounded-lg border border-line hover:border-navy hover:shadow-md transition-all group" data-id="element-218" data-mp="3f4pyj4g8hy87" data-mpi={index}>
                  <h3 className="text-lg font-heading font-bold text-ink-dark mb-2 group-hover:text-navy transition-colors" data-id="element-219" data-mp="2rd2f17laxpfy">
                    {useCase.title}
                  </h3>
                  <p className="text-ink-muted text-sm leading-relaxed mb-3" data-id="element-220" data-mp="2icw49tgzn3kh">
                    {useCase.desc}
                  </p>
                  <div className="flex items-center gap-1 text-navy text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-221" data-mp="2ab4ywmlxw8t5">
                    Learn More
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-222" data-mp="20m4zx37ftdey">
                      <path d="M6 12L10 8L6 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-223" data-mp="1rn9nvahpp93q" />
                    </svg>
                  </div>
                </motion.a>)}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CJI and NCJI */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-224" data-mp="37zyhoezn3ewp">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="bg-navy-deep text-white p-12 rounded-2xl" data-id="element-225" data-mp="tsp7wjq96ado">
          <h2 className="text-3xl font-heading font-bold mb-6" data-id="element-226" data-mp="17lyi2mm0yzx9">CJI and NCJI</h2>
          <p className="text-lg leading-relaxed opacity-90" data-id="element-227" data-mp="av60fkjg0ztd">
            Within SyNeural's Human Capability Platform, CJI serves as the core
            analytical layer. NCJI builds on that foundation to create a wider
            developmental, institutional, and leadership-readiness system.
            Together, they form a unified architecture for understanding,
            strengthening, and scaling judgment across the full human and
            institutional spectrum.
          </p>
        </motion.div>
      </section>
    </div>;
}